<script setup>
import { useTextStore } from '@/stores/texts'
import { watch } from 'vue'
import { ref } from 'vue'
import { inject } from 'vue'
import Toastify from 'toastify-js'

import format from 'string-format'

const base_url = import.meta.env.VITE_API_HOST

const props = defineProps({
  uid: {
    type: String,
    required: true
  }
})

const survey = ref(null)

const languageStore = useTextStore()

const setTitle = inject('setTitle')
const t = inject('t')

watch(
  () => props.uid,
  async () => {
    fetchSurvey()
  }
)

const topOption = ref(null)

async function fetchSurvey() {
  const data = await fetch(base_url + '/survey/' + props.uid + '/', {
    method: 'GET',
    headers: {
      'Accept-Language': languageStore.language
    }
  }).then((response) => response.json())

  setTitle(data.title)
  survey.value = data

  for (let option of survey.value.options) {
    if (!topOption.value || option.total > topOption.value.total) {
      topOption.value = option
    }
  }
}

languageStore.$subscribe(fetchSurvey)

function copyToClipboard() {
  navigator.clipboard.writeText(survey.value.share_url)
  Toastify({
    text: t('link-to-clipboard'),
    duration: 3000,
    gravity: 'bottom',
    position: 'center',
    style: {
      background: 'var(--color-signal)',
      color: '#fff'
    }
  }).showToast()
}

function shareEmail() {
  const subject = format(t('email-subject'), survey.value.title)
  const body = format(t('email-body'), survey.value.title, survey.value.share_url)
  window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`)
}

function shareWhatsApp() {
  const body = format(t('whatsapp-body'), survey.value.title, survey.value.share_url)
  window.open(`https://wa.me/?text=${encodeURIComponent(body)}`)
}

function share(type) {
  if (type === 'copy') {
    copyToClipboard()
  }
  if (type === 'email') {
    shareEmail()
  }

  if (type === 'whatsapp') {
    shareWhatsApp()
  }
  if (type === 'facebook') {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${survey.value.share_url}`)
  }
}
fetchSurvey()
</script>

<template>
  <article v-if="survey">
    <h2>{{ survey.title }}</h2>
    <p class="lead">{{ survey.lead }}</p>

    <section class="results">
      <div v-for="option in survey.options" :key="option.id" class="bar" :class="{ top: option.id === topOption.id }">
        <div class="filler" :style="{ width: (option.total / survey.total) * 100 + '%' }"></div>
        <span class="label">{{ option.name }}</span>
        <span class="percentage">{{ Math.round((option.total / survey.total) * 100) }} %</span>
      </div>
    </section>

    <section class="sharing">
      <h3>{{ t('share-title') }}</h3>
      <p>
        {{ t('share-text') }}
      </p>
      <div class="buttons">
        <button class="share whatsapp" @click="share('whatsapp')">
          <svg width="43" height="43" viewBox="0 0 43 43" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="21.3322" cy="21.6613" r="20.7322" fill="#34333C" stroke="#34333C" stroke-width="1.2" />
            <path
              d="M22.0138 8.66235C15.5902 8.66235 10.3609 13.8871 10.3586 20.3108C10.3574 22.3641 10.8952 24.3687 11.9149 26.135L10.3335 31.9957L16.438 30.5533C18.1402 31.482 20.0563 31.9695 22.007 31.9706H22.0116C28.4341 31.9706 33.6611 26.7447 33.6646 20.3222C33.6669 17.2084 32.456 14.2807 30.2557 12.078C28.0554 9.87654 25.1312 8.66352 22.0138 8.66235ZM22.0116 10.9957C24.5036 10.9969 26.8455 11.9685 28.606 13.7278C30.3665 15.4895 31.3336 17.8302 31.3312 20.3199C31.3289 25.4579 27.1496 29.6373 22.0093 29.6373C20.4541 29.6361 18.9136 29.2456 17.5568 28.5048L16.7707 28.0764L15.9025 28.2815L13.6056 28.8238L14.1662 26.7411L14.4191 25.8069L13.936 24.9683C13.1217 23.559 12.6907 21.9476 12.6919 20.3108C12.6942 15.1751 16.8747 10.9957 22.0116 10.9957ZM17.8895 14.9332C17.6947 14.9332 17.3796 15.0061 17.1125 15.2978C16.8453 15.5883 16.0916 16.2919 16.0916 17.7245C16.0916 19.1572 17.1353 20.5421 17.2811 20.7369C17.4258 20.9306 19.2955 23.9635 22.2554 25.1301C24.7147 26.0996 25.2142 25.9083 25.7485 25.8593C26.2829 25.8115 27.4723 25.1563 27.715 24.4762C27.9577 23.796 27.9582 23.2109 27.8859 23.0907C27.8136 22.9694 27.6193 22.8971 27.3276 22.7512C27.0371 22.6054 25.6055 21.9024 25.3384 21.8056C25.0712 21.7088 24.8758 21.6598 24.6821 21.9514C24.4885 22.2431 23.9319 22.8971 23.7616 23.0907C23.5912 23.2856 23.422 23.3118 23.1304 23.1659C22.8387 23.0189 21.9009 22.7107 20.7879 21.719C19.9223 20.9478 19.3381 19.9963 19.1678 19.7047C18.9986 19.4142 19.1519 19.2546 19.2977 19.1099C19.4284 18.9793 19.5871 18.7699 19.7329 18.5995C19.8776 18.4292 19.9277 18.3078 20.0246 18.1142C20.1214 17.9205 20.0718 17.7496 19.9995 17.6038C19.9272 17.4579 19.3607 16.0199 19.1017 15.4436C18.8836 14.9594 18.6531 14.9482 18.4455 14.94C18.2763 14.933 18.0832 14.9332 17.8895 14.9332Z"
              fill="white" />
          </svg>
        </button>
        <button class="share facebook" @click="share('facebook')">
          <svg width="44" height="43" viewBox="0 0 44 43" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="22.0178" cy="21.6613" r="20.7322" fill="#34333C" stroke="#34333C" stroke-width="1.2" />
            <g clip-path="url(#clip0_8603_31601)">
              <path
                d="M27.6837 9.84197C27.6837 9.76682 27.6862 9.70891 27.5894 9.69194C26.7307 9.54149 25.8725 9.37201 25.0007 9.34992C21.2862 9.0786 18.5978 11.478 18.6093 15.4711C18.6067 16.647 18.6037 17.8229 18.6071 18.9988C18.6075 19.1268 18.5746 19.156 18.4566 19.1555C17.2473 19.1507 16.0381 19.1541 14.8289 19.1499C14.7184 19.1495 14.6853 19.1775 14.6856 19.2979C14.6895 20.75 14.6896 22.2021 14.6855 23.6543C14.6852 23.7735 14.7149 23.8036 14.8273 23.8032C16.0365 23.7988 17.2457 23.8021 18.4549 23.7978C18.5667 23.7974 18.5975 23.8275 18.5973 23.9467C18.5936 27.0741 18.5916 30.2016 18.5905 33.3291H23.3264C23.3267 30.2027 23.3296 27.0762 23.3238 23.9498C23.1026 23.5415 27.1137 24.0769 26.9519 23.6697C27.1329 22.207 27.3178 20.7449 27.5077 19.2835C27.5232 19.1642 27.4915 19.1505 27.3934 19.1507C26.0894 19.1535 24.7854 19.151 23.4815 19.1551C23.1502 19.3057 23.3914 17.0347 23.3293 16.7089C23.347 13.5518 25.1098 13.5474 27.5502 13.6103C27.6547 13.611 27.6859 13.5844 27.6855 13.47C27.6814 12.2606 27.6827 11.0513 27.6837 9.84197Z"
                fill="white" />
            </g>
            <defs>
              <clipPath id="clip0_8603_31601">
                <rect width="13" height="24" fill="white" transform="translate(14.6855 9.3291)" />
              </clipPath>
            </defs>
          </svg>
        </button>
        <button class="share email" @click="share('email')">
          <svg width="43" height="43" viewBox="0 0 43 43" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="21.3322" cy="21.6613" r="20.7322" fill="#34333C" stroke="#34333C" stroke-width="1.2" />
            <rect x="12.8855" y="14.5291" width="17.6" height="13.6" fill="#34333C" stroke="white" stroke-width="2.4" />
            <path d="M30.6855 15.829L21.6855 21.329L12.5 15.5276" stroke="white" stroke-width="2.4" />
          </svg>
        </button>
        <button class="share copy" @click="share('copy')">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 43 43" height="43" width="43" fill="currentColor">
            <circle cx="21.3322" cy="21.6613" r="20.7322" fill="#34333C" stroke="#34333C" stroke-width="1.2" />
            <path d="M14.5 10.8291H30.5V29.8291" stroke="white" stroke-width="2.4" />
            <rect x="11.7" y="15.0291" width="14.6" height="17.6" fill="#34333C" stroke="white" stroke-width="2.4" />
          </svg>
        </button>
      </div>
    </section>
  </article>
</template>

<style scoped>
article {
  margin-top: 100px;
  margin-bottom: 120px;
}

h2 {
  font-size: 2.9rem;
  line-height: 99%;
  text-align: left;
  margin-bottom: 0.5em;
  margin-top: 0;
  letter-spacing: 3px;

  color: var(--color-signal);
}

.results {
  margin-top: 2rem;
  margin-bottom: 4rem;
}

.lead {
  color: var(--color-text);
  margin-bottom: 1em;
  text-align: left;
}

.bar {
  display: grid;
  grid-template-areas: 'content';
  grid-template-rows: auto;
  grid-template-columns: auto;
  border: solid 2px var(--color-signal);
  border-radius: 36px;
  height: 60px;
  margin-bottom: 0.8rem;
  contain: content;
  box-sizing: border-box;
}

.filler {
  background-color: var(--color-grey);
  color: white;
  padding: 10px;
  grid-area: content;
}

.label {
  grid-area: content;
  color: var(--color-text);
  padding-left: 2rem;
  align-self: center;
  max-width: calc(100% - 6em);
}

.percentage {
  grid-area: content;
  color: var(--color-text);
  padding-right: 2rem;
  align-self: center;
  text-align: right;
}

.bar.top {
  border-width: 3px;
}

.top .label,
.top .percentage {
  font-weight: bold;
  padding-left: calc(2rem - 1px);
  padding-right: calc(2rem - 1px);
}

.buttons {
  display: flex;
  justify-content: flex-start;
  gap: 0.5rem;
  margin-top: 2rem;
}

button.share {
  border-radius: 100%;
  border: none;
  background: var(--color-text);
  box-shadow: rgba(0, 0, 0, 0.3) 0px 2px 4px 0px;
  transition: transform 0.2s;
  aspect-ratio: 1 / 1;
  padding: 0;
}

button.share:hover {
  transform: scale(0.9);
}
</style>
